import { formatInstruction } from '../maneuvers/instructions';
import type { ManeuverState } from '../maneuvers/progress';
import type { LanguageCode, UnitSystem } from '../types';

export interface NextManeuverProps {
  maneuver: ManeuverState;
  language: LanguageCode;
  units: UnitSystem;
}

const labels: Record<LanguageCode, {
  first: string;
  live: string;
  unavailable: string;
  offRoute: string;
  routeKept: string;
  arrived: string;
}> = {
  EN: { first: 'First route instruction', live: 'Live route progress', unavailable: 'Maneuvers unavailable', offRoute: 'Off route', routeKept: 'Route kept · automatic rerouting is off', arrived: 'You have arrived' },
  DE: { first: 'Erster Routenhinweis', live: 'Live-Routenfortschritt', unavailable: 'Fahrhinweise nicht verfügbar', offRoute: 'Abseits der Route', routeKept: 'Route bleibt erhalten · automatische Neuberechnung ist aus', arrived: 'Ziel erreicht' },
  ES: { first: 'Primera instrucción de ruta', live: 'Progreso de ruta en directo', unavailable: 'Maniobras no disponibles', offRoute: 'Fuera de ruta', routeKept: 'Ruta conservada · recálculo automático desactivado', arrived: 'Has llegado' },
  IT: { first: 'Prima istruzione del percorso', live: 'Avanzamento percorso in tempo reale', unavailable: 'Manovre non disponibili', offRoute: 'Fuori percorso', routeKept: 'Percorso mantenuto · ricalcolo automatico disattivato', arrived: 'Sei arrivato' },
  FR: { first: 'Première instruction de l’itinéraire', live: 'Progression en direct', unavailable: 'Manœuvres indisponibles', offRoute: 'Hors itinéraire', routeKept: 'Itinéraire conservé · recalcul automatique désactivé', arrived: 'Vous êtes arrivé' }
};

function formatDistance(meters: number, units: UnitSystem): string {
  if (units === 'imperial') {
    const feet = meters * 3.28084;
    return feet < 1_000 ? `${Math.round(feet / 10) * 10} ft` : `${(meters / 1_609.344).toFixed(1)} mi`;
  }
  return meters < 1_000 ? `${Math.round(meters)} m` : `${(meters / 1_000).toFixed(1)} km`;
}

function maneuverSymbol(maneuver: ManeuverState): string {
  if (!maneuver.instruction) return '•';
  if (maneuver.instruction.sign < 0) return '↰';
  if (maneuver.instruction.sign > 0 && maneuver.instruction.sign !== 4) return '↱';
  return maneuver.instruction.sign === 4 ? '✓' : '↑';
}

export function NextManeuver({ maneuver, language, units }: NextManeuverProps) {
  const text = labels[language];
  if (maneuver.status === 'unavailable') {
    return <section className="next-maneuver" data-status="unavailable"><span aria-hidden="true">•</span><h3>{text.unavailable}</h3></section>;
  }
  if (maneuver.status === 'off_route') {
    return (
      <section className="next-maneuver" data-status="off_route">
        <span className="next-maneuver__symbol" aria-hidden="true">!</span>
        <div><h3>{text.offRoute}</h3><p>{text.routeKept}</p></div>
      </section>
    );
  }
  if (maneuver.status === 'finished') {
    return <section className="next-maneuver" data-status="finished"><span aria-hidden="true">✓</span><h3>{text.arrived}</h3></section>;
  }

  return (
    <section className="next-maneuver" data-status={maneuver.status}>
      <span className="next-maneuver__symbol" aria-hidden="true">{maneuverSymbol(maneuver)}</span>
      <div>
        <small>{maneuver.status === 'first' ? text.first : text.live}</small>
        <h3>{formatInstruction(maneuver.instruction, language)}</h3>
        {maneuver.distanceToInstructionM !== null ? <strong>{formatDistance(maneuver.distanceToInstructionM, units)}</strong> : null}
      </div>
    </section>
  );
}
